/**
 * Small presentational helpers used across the admin console.
 */
import { cn } from "@/lib/utils";

export function AdminPageHeader({ eyebrow, title, description, actions, testId }) {
    return (
        <header
            className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between"
            data-testid={testId}
        >
            <div>
                {eyebrow && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                        {eyebrow}
                    </p>
                )}
                <h1 className="mt-2 font-display text-4xl uppercase tracking-wide text-brand-offwhite md:text-5xl">
                    {title}
                </h1>
                {description && (
                    <p className="mt-2 max-w-2xl font-body text-sm text-brand-offwhite/70">
                        {description}
                    </p>
                )}
            </div>
            {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
        </header>
    );
}

export function AdminCard({ className, children, testId }) {
    return (
        <div
            className={cn(
                "border border-white/10 bg-white/[0.03] p-6",
                className,
            )}
            data-testid={testId}
        >
            {children}
        </div>
    );
}

export function StatCard({ label, value, tone = "neutral", testId }) {
    const toneClass = tone === "accent" ? "text-brand-gold" : "text-brand-offwhite";
    return (
        <AdminCard testId={testId}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-offwhite/60">
                {label}
            </p>
            <p className={cn("mt-3 font-display text-5xl leading-none", toneClass)}>
                {value ?? "—"}
            </p>
        </AdminCard>
    );
}

const STATUS_CLASSES = {
    new: "border-brand-gold text-brand-gold",
    shortlisted: "border-emerald-400 text-emerald-400",
    rejected: "border-rose-400 text-rose-400",
};

export function StatusPill({ status }) {
    const key = status?.toLowerCase() || "new";
    const cls = STATUS_CLASSES[key] || STATUS_CLASSES.new;
    return (
        <span
            className={cn(
                "inline-flex items-center border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.25em]",
                cls,
            )}
            data-testid={`status-pill-${key}`}
        >
            {key}
        </span>
    );
}

export function EmptyState({ title, hint, testId }) {
    return (
        <div
            className="border border-dashed border-white/15 p-10 text-center"
            data-testid={testId}
        >
            <p className="font-display text-2xl uppercase tracking-wide text-brand-offwhite/80">
                {title}
            </p>
            {hint && (
                <p className="mt-2 font-body text-sm text-brand-offwhite/60">{hint}</p>
            )}
        </div>
    );
}
